import React from 'react';
import { motion, Variants } from 'framer-motion';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

type ChoiceProps = {
  choice: string;
  isCorrect: boolean;
  onSelect: (choice: string) => void;
  isSelected: boolean;
  disabled?: boolean;
  showCorrectAnswer?: boolean;
};

const Choice: React.FC<ChoiceProps> = ({
  choice,
  isCorrect,
  onSelect,
  isSelected,
  disabled = false,
  showCorrectAnswer = false,
}) => {
  const handleClick = () => {
    if (!disabled) {
      onSelect(choice);
    }
  };

  // Animation variants
  const variants: Variants = {
    correct: {
      scale: [1, 1.2, 1],
      transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
    },
    incorrect: {
      x: [0, -10, 10, -10, 10, 0],
      transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
    },
    idle: {
      scale: 1,
      x: 0,
    },
  };

  return (
    <motion.div
      whileHover={{ scale: disabled ? 1 : 1.05 }}
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      variants={variants}
      animate={
        isSelected && !isCorrect
          ? 'incorrect'
          : showCorrectAnswer && isCorrect
            ? 'correct'
            : 'idle'
      }
      className={`flex flex-1 items-center p-4 border rounded-lg cursor-pointer transition-all duration-300
        ${
          (isSelected && !isCorrect) || (showCorrectAnswer && isCorrect)
            ? isCorrect
              ? 'border-green-500 bg-green-200 text-gray-800'
              : 'border-red-500 bg-red-200 text-gray-800'
            : 'border-gray-300'
        }`}
      onClick={handleClick}
    >
      <div className="flex-grow text-lg font-medium">{choice}</div>
      {(isSelected || showCorrectAnswer) && (isCorrect || isSelected) ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {isCorrect ? (
            <FaCheckCircle className="text-green-500" size={24} />
          ) : (
            <FaTimesCircle className="text-red-500" size={24} />
          )}
        </motion.div>
      ) : null}
    </motion.div>
  );
};

export default Choice;
